import React from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';

import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';

import DateWidget from '~/components/Common/Elements/DateWidget';

import { formatDateWithClock } from '~/services/TimeService';

type Props = {
  control: Control<CustomOfferFormValues>;
  register: UseFormRegister<CustomOfferFormValues>;
  errors: FieldErrors<CustomOfferFormValues>;
  hasOrders?: boolean;
  isSubmitting: boolean;
  isEditMode: boolean;
  price: number | null | undefined;
  customerDepositAmount: number | null | undefined;
  paymentDate?: string;
  showLegacyDepositFields: boolean;
  showLegacyFinalAmount: boolean;
};

export default function V1CustomerPaymentFields({
  control,
  register,
  errors,
  hasOrders,
  isSubmitting,
  isEditMode,
  price,
  customerDepositAmount,
  paymentDate,
  showLegacyDepositFields,
  showLegacyFinalAmount,
}: Props) {
  return (
    <>
      {showLegacyDepositFields && (
        <Grid mt={2} container columns={6} spacing={2}>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              {...register('payment_metadata.customer_deposit_1_amount')}
              helperText={errors.payment_metadata?.customer_deposit_1_amount?.message}
              error={!!errors.payment_metadata?.customer_deposit_1_amount}
              disabled={hasOrders || isSubmitting}
              label="Deposit amount (customer)"
              type="number"
              inputProps={{ step: 0.01 }}
              fullWidth
              required
              data-testid="customer-deposit-amount"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              value={price && customerDepositAmount ? Math.max(0, price - customerDepositAmount).toFixed(2) : '0.00'}
              label="Balance amount"
              type="number"
              fullWidth
              disabled
              data-testid="customer-balance-amount"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.customer_deposit_1_due_date"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <DateWidget
                  {...field}
                  slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                  disabled={hasOrders || isSubmitting}
                  label="Deposit due date"
                  disablePast
                  data-testid="customer-deposit-due-date"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.deposit_pi_number_customer"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  helperText={error?.message}
                  error={!!error}
                  disabled={isSubmitting}
                  label="Deposit PI number"
                  fullWidth
                  type="text"
                  data-testid="deposit-pi-number"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.customer_deposit_payment_made"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} data-testid="customer-deposit-payment-made" />}
                  label="Deposit payment made"
                />
              )}
            />
          </Grid>
        </Grid>
      )}
      {showLegacyFinalAmount && (
        <Grid mt={2} container columns={6} spacing={2}>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              {...register('payment_metadata.final_amount_customer')}
              helperText={errors.payment_metadata?.final_amount_customer?.message}
              error={!!errors.payment_metadata?.final_amount_customer}
              disabled
              label="Final amount (customer)"
              type="number"
              fullWidth
              required
              data-testid="final-amount-customer"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="due_date"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <DateWidget
                  {...field}
                  slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                  disabled={hasOrders || isSubmitting}
                  label="Final Amount due date - customer"
                  disablePast
                  data-testid="final-amount-due-date"
                />
              )}
            />
          </Grid>
          {isEditMode && (
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                disabled
                fullWidth
                value={paymentDate ? formatDateWithClock(paymentDate) : 'N/A'}
                label="Customer Paid Full Amount On"
                data-testid="customer-paid-full-amount-on"
              />
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
}
